import { combineReducers } from "redux";
import * as actions from "../actions";

// ===== 배송주문 관리 — 송장(발번/출력차수/발송처리/가송장/배송관리) =====
// 서버 미구현/미배포 — 응답 shape 미문서화라 raw 보관 + 화면에서 방어적 접근.

const initialStatus = {
  isFetching: false,
  error: null,
};

function makeStatus(fetchingTypes, successType, failureType) {
  return function status(state = initialStatus, action) {
    if (fetchingTypes.includes(action.type)) {
      return { isFetching: true, error: null };
    }
    switch (action.type) {
      case successType:
        return initialStatus;
      case failureType:
        return { isFetching: false, error: action.error };
      default:
        return state;
    }
  };
}

const status = makeStatus(
  [
    actions.GET_PRINT_TARGETS,
    actions.GET_SHIPPING_STAGE_ORDERS,
    actions.GET_PRINT_BATCHES,
    actions.GET_PROVISION_ORDERS,
    actions.TRACK_SHIPMENT,
    actions.CHECK_DELIVERY_DELAYS,
  ],
  actions.SET_SHIPMENT_STATUS_SUCCESS,
  actions.SET_SHIPMENT_STATUS_FAILURE,
);

// 발번/삭제/재발번/다박스/분할/배송정보변경/외부송장 — 송장번호를 만지는 커맨드 공용
const issueStatus = makeStatus(
  [
    actions.ISSUE_DELIVERY_TRACKING,
    actions.DELETE_DELIVERY_TRACKING,
    actions.REISSUE_DELIVERY_TRACKING,
    actions.ADD_SHIPMENT_BOX,
    actions.SPLIT_DELIVERY_SHIPMENT,
    actions.CHANGE_SHIPPING_AFTER_ISSUE,
    actions.APPLY_EXTERNAL_TRACKING,
  ],
  actions.SET_SHIPMENT_ISSUE_STATUS_SUCCESS,
  actions.SET_SHIPMENT_ISSUE_STATUS_FAILURE,
);

const pushStatus = makeStatus(
  [actions.PUSH_DELIVERY_SHIPMENT],
  actions.SET_SHIPMENT_PUSH_STATUS_SUCCESS,
  actions.SET_SHIPMENT_PUSH_STATUS_FAILURE,
);

const provisionStatus = makeStatus(
  [
    actions.ISSUE_PROVISION_TRACKING,
    actions.CONVERT_PROVISION_TO_REAL,
    actions.COMPLETE_PROVISION_DELIVERY,
  ],
  actions.SET_SHIPMENT_PROVISION_STATUS_SUCCESS,
  actions.SET_SHIPMENT_PROVISION_STATUS_FAILURE,
);

const batchStatus = makeStatus(
  [
    actions.CREATE_PRINT_BATCH,
    actions.REPRINT_BATCH,
    actions.EXPORT_PRINT_TARGETS_EXCEL,
  ],
  actions.SET_SHIPMENT_BATCH_STATUS_SUCCESS,
  actions.SET_SHIPMENT_BATCH_STATUS_FAILURE,
);

// 송장출력 대상(출력 대기) — listPrintTargets 응답 raw
function printTargets(state = [], action) {
  switch (action.type) {
    case actions.RECEIVE_PRINT_TARGETS:
      return Array.isArray(action.targets) ? action.targets : [];
    default:
      return state;
  }
}

// 보류 목록 — 활성 DELIVERY_HOLD 건(holdReasonCodes 부착). 목록 제외가 아니라
// 별도 분리 노출(우진 확정 2026-07-15) — 출력대기 테이블에 병합 표시 + 보류/사유 칩 필터
function printHolds(state = [], action) {
  switch (action.type) {
    case actions.RECEIVE_PRINT_TARGETS:
      return Array.isArray(action.holds) ? action.holds : [];
    default:
      return state;
  }
}

// 오류 목록 — reason: unpaid|receiver|address(+refineError). 화면 구분 노출은 추후
// 범위(erp-audit 문서) — 데이터만 보관
function printErrors(state = [], action) {
  switch (action.type) {
    case actions.RECEIVE_PRINT_TARGETS:
      return Array.isArray(action.errors) ? action.errors : [];
    default:
      return state;
  }
}

// 최초 목록 수신 게이트 — status 는 여러 트리거(GET_PRINT_TARGETS/GET_PRINT_BATCHES 등)가
// 공유하는 단일 플래그라, 먼저 끝난 다른 요청의 SUCCESS 가 listPrintTargets 응답 전에
// isFetching 을 풀어 '0건' 빈 문구 프레임이 노출된다. 페이지는 게이트가 열리기 전까지
// 로딩을 유지한다. FAILURE 도 게이트 해제 — 미수신 스피너 고착 방지(실패 액션도 공유라
// 과해제 가능하나, 그 경우 빈 문구 노출 = 기존 동작과 동일).
function targetsLoaded(state = false, action) {
  switch (action.type) {
    case actions.RECEIVE_PRINT_TARGETS:
    case actions.SET_SHIPMENT_STATUS_FAILURE:
      return true;
    default:
      return state;
  }
}

// 차수 이력 동일 게이트 — 조회조건 복원(activeTab="batches")으로 첫 화면이 이력 탭일 수 있음
function batchesLoaded(state = false, action) {
  switch (action.type) {
    case actions.RECEIVE_PRINT_BATCHES:
    case actions.SET_SHIPMENT_STATUS_FAILURE:
      return true;
    default:
      return state;
  }
}

// 배송 관리 리스트 — listDeliveryStageOrdersByTab (탭 단위 보관)
function stageTab(state = { tab: "standby", orders: [] }, action) {
  switch (action.type) {
    case actions.RECEIVE_SHIPPING_STAGE_ORDERS:
      return {
        tab: action.tab,
        orders: Array.isArray(action.orders) ? action.orders : [],
      };
    default:
      return state;
  }
}

// 출력 차수(TrackingPrintHistory) 목록 — seq "YYYYMMDD-NN"
function printBatches(state = [], action) {
  switch (action.type) {
    case actions.RECEIVE_PRINT_BATCHES:
      return Array.isArray(action.batches) ? action.batches : [];
    default:
      return state;
  }
}

// 가송장(provision=true) 주문 목록
function provisionOrders(state = [], action) {
  switch (action.type) {
    case actions.RECEIVE_PROVISION_ORDERS:
      return Array.isArray(action.orders) ? action.orders : [];
    default:
      return state;
  }
}

function issueResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_ISSUE_TRACKING_RESULT:
      return action.result;
    case actions.CLEAR_ISSUE_TRACKING_RESULT:
    case actions.ISSUE_DELIVERY_TRACKING:
      return null;
    default:
      return state;
  }
}

function pushResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_PUSH_SHIPMENT_RESULT:
      return action.result;
    case actions.PUSH_DELIVERY_SHIPMENT:
      return null;
    default:
      return state;
  }
}

function applyExternalResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_APPLY_EXTERNAL_TRACKING_RESULT:
      return action.result;
    case actions.APPLY_EXTERNAL_TRACKING:
      return null;
    default:
      return state;
  }
}

function provisionResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_PROVISION_RESULT:
      return action.result;
    case actions.ISSUE_PROVISION_TRACKING:
    case actions.CONVERT_PROVISION_TO_REAL:
    case actions.COMPLETE_PROVISION_DELIVERY:
      return null;
    default:
      return state;
  }
}

// 저빈도 커맨드(삭제/분할/다박스/재발번/배송정보변경) 공용 결과 — {command, result}
function commandResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_SHIPMENT_COMMAND_RESULT:
      return { command: action.command, result: action.result };
    default:
      return state;
  }
}

function printBatchResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_PRINT_BATCH_RESULT:
      return action.result;
    case actions.CREATE_PRINT_BATCH:
    case actions.REPRINT_BATCH:
      return null;
    default:
      return state;
  }
}

// exportPrintTargetsExcel — S3 presigned URL
function exportUrl(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_EXPORT_PRINT_TARGETS_URL:
      return action.url;
    case actions.EXPORT_PRINT_TARGETS_EXCEL:
      return null;
    default:
      return state;
  }
}

// 단건 배송조회(trackShipment) — 조회 모달 표시용
function trackResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_TRACK_SHIPMENT_RESULT:
      return action.result;
    case actions.CLEAR_TRACK_SHIPMENT_RESULT:
    case actions.TRACK_SHIPMENT:
      return null;
    default:
      return state;
  }
}

function delays(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_DELIVERY_DELAYS:
      return action.result;
    default:
      return state;
  }
}

// 발번 성공 낙관 숨김 id — 발번 직후 재조회(listPrintTargets)가 DynamoDB GSI 지연으로
// 송장번호 없는 stale 행을 돌려줘 "발번 성공 1건"인데 출력 대기에 그대로 남아 보였다
// (2026-09-08 실측). delivery.mergeHiddenOrderIds 와 같은 방식: 성공 주문 id 를 등록해
// 걸러내고, 신선한 응답에서 그 주문이 빠지면(=서버 반영 완료) id 도 자동 정리한다.
// 결과 shape 은 실송장·가송장 공통 {results:{[orderId]:{issued:[...]}}} — issued 가 빈 주문
// (재고 부족·오류)은 출력 대기 잔류가 맞으므로 숨기지 않는다.
const issuedIdsOf = (result) => {
  const results = (result && typeof result === "object" && result.results) || {};
  return Object.keys(results).filter((oid) => {
    const r = results[oid];
    return r && Array.isArray(r.issued) && r.issued.length > 0;
  });
};

function issuedHiddenOrderIds(state = [], action) {
  switch (action.type) {
    case actions.RECEIVE_ISSUE_TRACKING_RESULT:
    case actions.RECEIVE_PROVISION_RESULT: {
      const ids = issuedIdsOf(action.result);
      if (ids.length === 0) return state;
      return Array.from(new Set([...state, ...ids]));
    }
    case actions.RECEIVE_PRINT_TARGETS: {
      if (state.length === 0) return state;
      const present = new Set((action.targets || []).map((t) => t && t.id));
      const kept = state.filter((id) => present.has(id));
      return kept.length === state.length ? state : kept;
    }
    default:
      return state;
  }
}

const combined = combineReducers({
  status,
  issueStatus,
  pushStatus,
  provisionStatus,
  batchStatus,
  printTargets,
  printHolds,
  printErrors,
  targetsLoaded,
  batchesLoaded,
  stageTab,
  printBatches,
  provisionOrders,
  issueResult,
  pushResult,
  applyExternalResult,
  provisionResult,
  commandResult,
  printBatchResult,
  exportUrl,
  trackResult,
  delays,
  issuedHiddenOrderIds,
});

// 최상위 후처리 — 슬라이스는 서로를 못 보므로 여기서 printTargets 에 낙관 숨김을 적용한다.
export default function shipment(state, action) {
  const next = combined(state, action);
  if (next.issuedHiddenOrderIds.length === 0) return next;
  const crossActions = [
    actions.RECEIVE_ISSUE_TRACKING_RESULT,
    actions.RECEIVE_PROVISION_RESULT,
    actions.RECEIVE_PRINT_TARGETS,
  ];
  if (!crossActions.includes(action.type)) return next;
  const hidden = new Set(next.issuedHiddenOrderIds);
  const visible = next.printTargets.filter((row) => !hidden.has(row && row.id));
  return visible.length === next.printTargets.length
    ? next
    : { ...next, printTargets: visible };
}
